import React, { useState } from "react";
import OtpInput from "./OtpInput";
import {
  firstTimeResetPasswordGenerateOTP,
  passwordResetPassword,
} from "@/service/login.service";
import { toast } from "react-toastify";
import { handleAxiosError } from "@/lib/errorHandler";

function PasswordResetOTPModal({
  isOpen,
  onClose,
  userName,
  currentPassword,
  newPassword,
  confirmPassword,
  newApin,
  confirmApin,
}) {
  const [otp, setOtp] = useState(Array(6).fill(""));

  /**
   * Handles input change for OTP fields.
   *
   * @param {string} value - The digit entered
   * @param {number} index - Index of the current OTP input
   */
  const handleInputChange = (value, index) => {
    if (/^\d?$/.test(value)) {
      setOtp((prevOtp) => {
        const updatedOtp = [...prevOtp];
        updatedOtp[index] = value;
        return updatedOtp;
      });

      if (value && index <= 5) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  /**
   * Handles backspace key press in OTP inputs.
   *
   * @param {number} index - Index of the OTP input field
   */
  const handleBackspace = (index) => {
    setOtp((prevOtp) => {
      const updatedOtp = [...prevOtp];
      if (updatedOtp[index] === "" && index > 0) {
        const prevInput = document.getElementById(`otp-input-${index - 1}`);
        if (prevInput) prevInput.focus();
      }
      updatedOtp[index] = "";
      return updatedOtp;
    });
  };

  /**
   * Calls API to generate OTP for password reset.
   */
  const generateOTP = async () => {
    try {
      await firstTimeResetPasswordGenerateOTP({
        userName: userName?.toUpperCase(),
        currentPassword,
        newPassword,
        confirmPassword,
        newApin,
        confirmApin,
      });
      toast.success("OTP resent successfully!");
    } catch (error) {
      handleAxiosError(error);
    }
  };

  /**
   * Handles form submission to verify OTP and change password.
   *
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      handleAxiosError(null,{"customMessage": "Please enter a valid 6-digit OTP."});
      return;
    }
    changePassword(otpCode);
  };

  /**
   * Calls API to update the first time reset password using OTP and new credentials.
   *
   * @param {string} otp - 6-digit OTP code
   */
  const changePassword = async (otp) => {
    try {
      await passwordResetPassword({
        userName: userName?.toUpperCase(),
        currentPassword,
        newPassword,
        confirmPassword,
        newApin,
        confirmApin,
        otp,
      });
      toast.success("Password changed successfully!");
      onClose();
    } catch (error) {
      handleAxiosError(error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-white rounded-lg shadow-md w-[35%] p-6">
      <h2 className=" text-xl font-semibold mb-4">Reset Password</h2>
      <p className="mb-2 text-sm text-gray-700">
        Enter the code sent to{" "}
        <span className="underline font-semibold">{userName}</span> associated
        email to reset your newPassword.
      </p>

      {/* OTP Inputs */}
      <div className="flex justify-center my-6 space-x-2">
        {otp.map((value, index) => (
          <OtpInput
            key={index}
            value={value}
            index={index}
            onChange={handleInputChange}
            onBackspace={handleBackspace}
          />
        ))}
      </div>

      {/* Resend Option */}
      <p className="text-gray-600 mt-4 flex justify-end mx-3">
        Didn&apos;t receive the code?{" "}
        <button
          onClick={generateOTP}
          className="text-orange-500 font-semibold hover:underline mx-3"
        >
          Resend OTP
        </button>
      </p>

      <div className="flex justify-end gap-2 my-5">
        <button
          onClick={onClose}
          className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          disabled={!otp}
          type="submit"
          onClick={(e) => handleSubmit(e)}
          className={`px-4 py-2 rounded text-white ${
            otp
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Reset password
        </button>
      </div>
    </div>
  );
}

export default PasswordResetOTPModal;
